import type { WebinarDetail, WebinarStep } from "@xop/dashboard-shared";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import { Alert, Box, Button, Chip, Typography } from "@mui/material";
import type { JSX, ReactNode } from "react";
import { Link as RouterLink } from "react-router-dom";

/**
 * Pieces every wizard step reuses.
 *
 * The design rule the whole surface follows: **one step, one instruction, one
 * primary button.** A step never shows two equally-weighted actions, and it
 * always says in plain words what pressing the button will do — including,
 * every single time, that nothing is emailed until a human presses Send in
 * the campaign editor.
 */

export interface StepProps {
  detail: WebinarDetail;
  /** Re-read the whole webinar after a mutation. */
  refresh: () => void;
  canEdit: boolean;
  /** Tick this step off and move the wizard on. */
  markDone: (step: WebinarStep) => void;
}

/** The line every campaign-creating step ends with. Written once so the
 * promise is identical in all three places — this is the feature's single
 * most important safety property. */
export const NOTHING_IS_SENT =
  "This only creates a draft. Nothing is emailed to anyone until you open it and press Send yourself.";

export function SafetyNote(): JSX.Element {
  return (
    <Alert severity="info" icon={false} sx={{ fontSize: 13 }}>
      {NOTHING_IS_SENT}
    </Alert>
  );
}

/** What to do here, in one sentence, above the controls. */
export function StepIntro({ children }: { children: ReactNode }): JSX.Element {
  return (
    <Typography sx={{ color: "text.secondary", fontSize: 14, mb: 2 }}>{children}</Typography>
  );
}

/** A green "you already did this" line with the detail of what happened. */
export function DoneNote({ children }: { children: ReactNode }): JSX.Element {
  return (
    <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 2 }}>
      <CheckCircleIcon color="success" fontSize="small" />
      <Typography sx={{ fontSize: 14 }}>{children}</Typography>
    </Box>
  );
}

/** Link out to a campaign in the Email Campaigns editor. The one place a
 * webinar hands the operator over to the tool they already know. */
export function OpenCampaignButton({
  campaignId,
  label,
}: {
  campaignId: number;
  label: string;
}): JSX.Element {
  return (
    <Button
      component={RouterLink}
      to={`/marketing/campaigns/edit/${campaignId}`}
      variant="contained"
      endIcon={<OpenInNewIcon />}
      data-testid="open-campaign"
    >
      {label}
    </Button>
  );
}

/** Live state of a linked campaign — or an honest "could not check". */
export function CampaignStatus({
  summary,
}: {
  summary: WebinarDetail["campaigns"][string] | undefined;
}): JSX.Element | null {
  if (summary === undefined) return null;
  if (summary === null) {
    // Deliberately not "0 sent": we could not reach the CRM, and a confident
    // zero would be a lie the operator might act on.
    return <Chip size="small" variant="outlined" label="Could not check the CRM" />;
  }
  const sent = summary.status === "sent" || summary.sent_count > 0;
  return (
    <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
      <Chip
        size="small"
        color={sent ? "success" : "default"}
        variant={sent ? "filled" : "outlined"}
        label={sent ? `Sent to ${summary.sent_count}` : "Draft - not sent yet"}
      />
      {summary.recipients_count > 0 && (
        <Chip size="small" variant="outlined" label={`${summary.recipients_count} recipients`} />
      )}
      {summary.opened_count > 0 && (
        <Chip size="small" variant="outlined" label={`${summary.opened_count} opened`} />
      )}
      {summary.clicked_count > 0 && (
        <Chip size="small" variant="outlined" label={`${summary.clicked_count} clicked`} />
      )}
    </Box>
  );
}

/** Bottom-of-step "I'm finished here" control. Every step has one, so the
 * operator always knows how to move on even when a step is optional. */
export function StepFooter({
  step,
  detail,
  markDone,
  canEdit,
  disabledReason,
}: {
  step: WebinarStep;
  detail: WebinarDetail;
  markDone: (step: WebinarStep) => void;
  canEdit: boolean;
  /** When set, the button is disabled and this explains why. */
  disabledReason?: string;
}): JSX.Element {
  const done = detail.webinar.completed_steps.includes(step);
  if (done) {
    return (
      <Box sx={{ mt: 3, display: "flex", alignItems: "center", gap: 1 }}>
        <CheckCircleIcon color="success" fontSize="small" />
        <Typography sx={{ fontSize: 13, color: "text.secondary" }}>
          This step is done.
        </Typography>
      </Box>
    );
  }
  return (
    <Box sx={{ mt: 3, display: "flex", alignItems: "center", gap: 2, flexWrap: "wrap" }}>
      <Button
        variant="outlined"
        onClick={() => markDone(step)}
        disabled={!canEdit || !!disabledReason}
        data-testid={`mark-done-${step}`}
      >
        Done, next step
      </Button>
      {disabledReason && (
        <Typography sx={{ fontSize: 13, color: "text.secondary" }}>{disabledReason}</Typography>
      )}
    </Box>
  );
}
